import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useI18n } from "~/i18n";
import { resolveIpAssets } from "~/theme/resolve-theme";
import { SectionHeading } from "~/components/sections/section-heading";
import type { Character } from "~/types/ip";

interface CharactersProps {
  characters: Character[];
  focusCharacterId?: number | null;
}

function AnimatedName({ name }: { name: string }) {
  return (
    <span className="characters__name-letters inline-block">
      {[...name].map((char, i) => (
        <span
          key={i}
          className="characters__name-letter inline-block opacity-0 animate-[nameLetterIn_0.6s_cubic-bezier(0.22,1,0.36,1)_forwards]"
          style={{ animationDelay: `${0.15 + i * 0.06}s` }}
        >
          {char === " " ? "\u00A0" : char}
        </span>
      ))}
    </span>
  );
}

export function Characters({ characters, focusCharacterId }: CharactersProps) {
  const { locale } = useI18n();
  const { slug = "" } = useParams<{ slug: string }>();
  const assets = resolveIpAssets(slug);
  const [activeId, setActiveId] = useState(characters[0]?.id ?? 1);

  useEffect(() => {
    if (focusCharacterId !== null && focusCharacterId !== undefined) {
      setActiveId(focusCharacterId);
    }
  }, [focusCharacterId]);

  const active = characters.find((c) => c.id === activeId) ?? characters[0];
  if (!active) return null;

  const activeGroup = active.group;
  const groupChars = characters.filter((c) => c.group === activeGroup);

  const handleTabClick = (c: Character) => {
    const firstInGroup = characters.find((ch) => ch.group === c.group);
    if (firstInGroup) setActiveId(firstInGroup.id);
  };

  return (
    <section
      id="character"
      className="characters relative z-[2] overflow-hidden bg-white"
      style={{ contain: "paint" }}
    >
      <div className="mx-auto max-w-full px-[clamp(24px,5vw,120px)] pt-[60px] pb-[80px]">
        <SectionHeading imgSrc={assets.sectionTitles.characters} alt="Characters" noMargin />

        {/* Tabs */}
        <div className="characters__tabs flex justify-center gap-4 pt-12">
          {/* Single characters (group a) */}
          {characters
            .filter((c) => c.group === "a")
            .map((c) => (
              <button
                key={c.id}
                className={`characters__tab flex flex-col items-center gap-2.5 transition-opacity ${activeGroup === c.group ? "characters__tab--active opacity-100" : "opacity-40 hover:opacity-70"}`}
                onClick={() => handleTabClick(c)}
              >
                <div
                  className={`characters__tab-face flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-full border-2 text-lg leading-normal font-semibold text-[var(--ip-primary)] transition-all ${
                    activeGroup === c.group
                      ? "border-[var(--ip-primary)] shadow-[0_0_0_3px_var(--ip-primary-soft)]"
                      : "border-transparent"
                  }`}
                  style={{ background: c.color }}
                >
                  {c.faceImgs[0] ? (
                    <img
                      src={c.faceImgs[0]}
                      alt={c.name[locale]}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{c.name[locale].slice(-1)}</span>
                  )}
                </div>
                <p
                  className={`characters__tab-name text-[11px] leading-normal tracking-[0.08em] ${activeGroup === c.group ? "font-medium text-[var(--ip-text)]" : "text-[var(--ip-text-muted)]"}`}
                >
                  {c.name[locale]}
                </p>
              </button>
            ))}

          {/* Duo characters (group bc) */}
          {(() => {
            const bcChars = characters.filter((c) => c.group === "bc");
            if (bcChars.length === 0) return null;
            return (
              <button
                className={`characters__tab characters__tab--duo flex flex-col items-center gap-2.5 transition-opacity ${activeGroup === "bc" ? "characters__tab--active characters__tab--ochre opacity-100" : "opacity-40 hover:opacity-70"}`}
                onClick={() => handleTabClick(bcChars[0]!)}
              >
                <div
                  className={`characters__tab-face characters__tab-face--duo flex h-[72px] w-[144px] overflow-hidden rounded-full border-2 transition-all ${
                    activeGroup === "bc"
                      ? "border-[rgba(190,130,48,0.5)] shadow-[0_0_0_3px_rgba(190,130,48,0.1)]"
                      : "border-transparent"
                  }`}
                  style={{ background: bcChars[0]!.color }}
                >
                  {bcChars.map((c) => (
                    <div
                      key={c.id}
                      className="characters__tab-face-half -ml-2 flex h-[72px] w-[72px] items-center justify-center overflow-hidden first:ml-0"
                    >
                      {c.faceImgs[0] ? (
                        <img
                          src={c.faceImgs[0]}
                          alt={c.name[locale]}
                          className="w-[72px] h-[72px] object-cover"
                        />
                      ) : (
                        <span className="text-lg font-semibold text-[var(--ip-primary)]">
                          {c.name[locale].slice(-1)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <p
                  className={`characters__tab-name text-[11px] leading-normal tracking-[0.08em] ${activeGroup === "bc" ? "font-medium text-[var(--ip-text)]" : "text-[var(--ip-text-muted)]"}`}
                >
                  {bcChars.map((c) => c.name[locale]).join(" & ")}
                </p>
              </button>
            );
          })()}
        </div>

        {/* Panel */}
        <div className="characters__panel relative mt-16 max-md:mt-8 w-full">
          {/* Ellipse background — 데스크톱에서는 카드만큼 왼쪽으로 이동해 캐릭터 중앙에 위치 */}
          <div
            key={`ellipse-${activeId}`}
            className="characters__ellipse absolute top-1/2 left-1/2 md:left-[calc(50%-180px)] z-0 h-[320px] w-[520px] max-md:h-[220px] max-md:w-[340px] -translate-x-1/2 -translate-y-1/2 rotate-[15deg] overflow-hidden rounded-[50%] pointer-events-none animate-[chara-ellipse-pulse_700ms_cubic-bezier(0.34,1.56,0.64,1)_both]"
          >
            <div
              className="characters__ellipse-bg absolute inset-0"
              style={{ backgroundColor: active.ellipseColor }}
            />
            <div className="characters__ellipse-grid absolute -inset-[40%] -rotate-[15deg] bg-[linear-gradient(rgba(255,255,255,0.25)_2px,transparent_2px),linear-gradient(90deg,rgba(255,255,255,0.25)_2px,transparent_2px)] bg-[length:28px_28px]" />
          </div>

          {/* Content: 캐릭터 이미지 + 데스크톱 사이드 카드 (이미지+카드를 한 묶음으로 중앙 정렬) */}
          <div
            key={activeId}
            className="characters__panel-inner relative flex items-end justify-center min-h-[700px] max-md:min-h-0 md:gap-10"
          >
            <div className="characters__full shrink-0 self-end relative z-[1] h-[880px] w-[580px] max-md:h-[400px] max-md:w-[260px] max-md:mx-auto animate-[chara-img-enter_1s_cubic-bezier(0.22,1,0.36,1)_0.1s_both]">
              {active.fullImg ? (
                <img
                  src={active.fullImg}
                  alt={active.name[locale]}
                  className="block w-full h-full object-contain object-bottom"
                  style={{ filter: `drop-shadow(12px 0px 0px ${active.shadowColor})` }}
                />
              ) : (
                <div className="characters__img-placeholder w-full aspect-[2/3] bg-white/30" />
              )}
            </div>

            {/* 데스크톱 전용 사이드 카드 (이미지 우측, 이미지와 함께 중앙 정렬) */}
            <div className="characters__side-cards hidden md:flex md:flex-col md:gap-12 md:self-center md:w-[320px] md:min-w-0 md:relative md:z-[2]">
              {(active.textSide === "right" ? [active] : groupChars).map((c) => (
                <div
                  key={c.id}
                  className="characters__card min-w-0 animate-[chara-text-enter-right_0.8s_ease_both]"
                >
                  <h3 className="characters__name text-[32px] font-bold tracking-[0.02em] leading-[1.1] text-[#3d3d3d] break-words">
                    <AnimatedName name={c.name[locale]} />
                  </h3>
                  <div className="characters__divider mt-5 h-px w-10 bg-[#3d3d3d] opacity-40" />
                  <p className="characters__cv mt-5 text-[13px] font-medium leading-[1.7] tracking-[0.08em] text-[#3d3d3d]">
                    {c.cv[locale]}
                  </p>
                  <div className="characters__desc mt-4 flex flex-col gap-3 text-sm font-light leading-[2] text-[#3d3d3d] break-words">
                    {c.description[locale].split(/\n\n+/).map((para, i) => (
                      <p key={i} className="whitespace-pre-line">
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 정보 영역 — 모바일 전용 (데스크톱은 사이드 카드로 노출) */}
        <div
          key={`info-${activeId}`}
          className="characters__info-area hidden max-md:block px-8 pt-6 pb-10"
        >
          {active.textSide === "right" ? (
            <div className="mx-auto flex max-w-[560px] flex-col gap-3 text-center animate-[chara-text-enter_0.8s_ease_both]">
              <h3 className="characters__name text-[32px] max-md:text-2xl font-bold tracking-[0.05em] text-[#3d3d3d]">
                <AnimatedName name={active.name[locale]} />
              </h3>
              <p className="characters__cv text-[13px] leading-[1.7] tracking-[0.08em] text-[#3d3d3d]">
                {active.cv[locale]}
              </p>
              <div className="characters__divider mx-auto h-px w-8 bg-[#3d3d3d] opacity-40" />
              <div className="characters__desc flex flex-col gap-3 break-words text-sm font-light leading-[2] text-[#3d3d3d]">
                {active.description[locale].split(/\n\n+/).map((para, i) => (
                  <p key={i} className="whitespace-pre-line">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto grid max-w-[1080px] grid-cols-2 gap-x-16 gap-y-10 max-md:grid-cols-1 max-md:gap-6">
              {groupChars.map((c) => (
                <div
                  key={c.id}
                  className="flex flex-col gap-3 max-md:text-center animate-[chara-text-enter-right_0.8s_ease_both]"
                >
                  <h3 className="characters__name text-[32px] max-md:text-2xl font-bold tracking-[0.05em] text-[#3d3d3d]">
                    <AnimatedName name={c.name[locale]} />
                  </h3>
                  <p className="characters__cv text-[13px] leading-[1.7] tracking-[0.08em] text-[#3d3d3d]">
                    {c.cv[locale]}
                  </p>
                  <div className="characters__divider h-px w-8 bg-[#3d3d3d] opacity-40 max-md:mx-auto" />
                  <div className="characters__desc flex flex-col gap-3 break-words text-sm font-light leading-[2] text-[#3d3d3d]">
                    {c.description[locale].split(/\n\n+/).map((para, i) => (
                      <p key={i} className="whitespace-pre-line">
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
